import {
  AfterContentInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BooleanInput, toBoolean } from '@ngx-simple/core/coercion';
import { SimButtonComponent } from '@ngx-simple/simple-ui/button';
import { merge, of, Subscription } from 'rxjs';
import { SimDatepickerBase, SimDatepickerControl } from './datepicker-base';

@Directive({
  selector: '[simDatepickerToggleIcon]'
})
export class SimDatepickerToggleIconDirective {}

@Component({
  selector: 'sim-datepicker-toggle',
  templateUrl: './datepicker-toggle.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'simDatepickerToggle',
  host: {
    class: 'sim-datepicker-toggle',
    '[attr.tabindex]': 'disabled ? null : -1',
    '[class.mat-datepicker-toggle-active]': 'datepicker && datepicker.opened',
    '[class.mat-accent]': 'datepicker && datepicker.color === "accent"',
    '[class.mat-warn]': 'datepicker && datepicker.color === "warn"',
    '(focus)': '_button.focus()'
  }
})
export class SimDatepickerToggleComponent<D> implements AfterContentInit, OnChanges, OnDestroy {
  private _stateChanges = Subscription.EMPTY;

  /** 绑定切换按钮的Datepicker实例 */
  // tslint:disable-next-line: no-input-rename
  @Input('for') datepicker: SimDatepickerBase<SimDatepickerControl<any>, D>;

  /** 用于切换按钮的 tabindex */
  @Input() tabIndex: number | null;

  /** 切换按钮是否被禁用 */
  @Input()
  get disabled(): boolean {
    if (this._disabled === undefined && this.datepicker) {
      return this.datepicker.disabled;
    }

    return !!this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = toBoolean(value);
  }
  private _disabled: boolean;

  /** 由使用者设置的自定义图标 */
  @ContentChild(SimDatepickerToggleIconDirective) _customIcon: SimDatepickerToggleIconDirective;

  /** 原生的按钮元素 */
  @ViewChild('button') _button: SimButtonComponent;

  constructor(private _changeDetectorRef: ChangeDetectorRef, @Attribute('tabindex') defaultTabIndex: string) {
    const parsedTabIndex = Number(defaultTabIndex);
    this.tabIndex = parsedTabIndex || parsedTabIndex === 0 ? parsedTabIndex : null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['datepicker']) {
      this._watchStateChanges();
    }
  }

  ngAfterContentInit() {
    this._watchStateChanges();
  }

  ngOnDestroy() {
    this._stateChanges.unsubscribe();
  }

  _open(event: Event): void {
    if (this.datepicker && !this.disabled) {
      this.datepicker.open();
      event.stopPropagation();
    }
  }

  private _watchStateChanges() {
    const datepickerStateChanged = this.datepicker ? this.datepicker._stateChanges : of();
    const inputStateChanged = this.datepicker && this.datepicker._datepickerInput ? this.datepicker._datepickerInput.stateChanges : of();
    const datepickerToggled = this.datepicker ? merge(this.datepicker.opened, this.datepicker.closed) : of();

    this._stateChanges.unsubscribe();
    this._stateChanges = merge(datepickerStateChanged, inputStateChanged, datepickerToggled).subscribe(() =>
      this._changeDetectorRef.markForCheck()
    );
  }

  static ngAcceptInputType_disabled: BooleanInput;
}
